/**
 * Hook: useScrollToSection
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R22
 *
 * Description: Scroll-to-section effect for the BROWSE-V1 viewer.
 *              When `ready` is true and `sectionId` is present, looks
 *              up the matching `#cnt_<sectionId>` element (the DOM
 *              contract R05 ships) and calls `scrollIntoView` on it.
 *              When `sectionId` is null, no-ops so the viewer stays
 *              scrolled to top — that's the explicit R22 behaviour
 *              for the entry-from-browse-list path.
 *
 *              The hook waits one animation frame before scrolling
 *              so the SafeHtml children have time to paint. Without
 *              that hop, the scroll target exists in the DOM but the
 *              browser hasn't laid out its offsetTop yet and the
 *              scroll lands at 0.
 *
 *              Re-runs whenever the inputs change so later section
 *              hash updates (e.g. React Router navigation to
 *              `/browse/guidelines/407?sectionId=99`) still drive
 *              the scroll — the hook does not memoise away the
 *              repeated invocation.
 *
 * Risk Links: R7
 * FMEA Links: FM7
 */

import { useEffect } from 'react';

interface UseScrollToSectionOptions {
  /**
   * Section id parsed from the `?sectionId=` query param. `null`
   * means "no explicit target" and the hook no-ops so the viewer
   * stays at the top of the page.
   */
  sectionId: number | null;
  /**
   * True once the guideline payload has arrived and at least one
   * narrowed section is about to be mounted. Until this flips the
   * hook cannot scroll because the target `#cnt_<sectionId>` does
   * not exist in the DOM yet.
   */
  ready: boolean;
}

export function useScrollToSection({
  sectionId,
  ready,
}: UseScrollToSectionOptions): void {
  useEffect(() => {
    if (!ready || sectionId === null) {
      return;
    }
    const targetId = `cnt_${sectionId}`;
    // Hop one animation frame so SafeHtml children have painted
    // before we compute scroll position.
    const handle = requestAnimationFrame(() => {
      const el = document.getElementById(targetId);
      if (!el) {
        if (import.meta.env.DEV) {
          console.warn(
            `[BROWSE-V1-R22] Scroll target #${targetId} not found; ` +
              `guideline may have no matching sectionContent entry.`,
          );
        }
        return;
      }
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => {
      cancelAnimationFrame(handle);
    };
  }, [sectionId, ready]);
}
